import axios from 'axios';
import type { AxiosError } from 'axios';

export type ApiError = AxiosError<{ error?: string }>;

export function getApiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(err)) {
    const data = (err as ApiError).response?.data;
    if (data?.error) return data.error;
  }
  return fallback;
}

/**
 * How a pending post-auth action (invite-link claim, self-enroll) should treat
 * a failure.
 *
 * A server RESPONSE is a definitive answer — revoked link, expired link, no
 * seats left — so the queued action should be dropped and the server's own
 * wording shown; those messages are already written for a human. NO response
 * means the request never landed (offline, proxy, DNS), so the action stays
 * queued for a retry. Either way the learner is told, because from their side
 * an empty dashboard looks identical to a broken link.
 */
export function classifyPendingActionError(
  err: unknown,
  messages: { definitiveFallback: string; unreachable: string },
): { definitive: boolean; message: string } {
  if (axios.isAxiosError(err) && err.response) {
    return { definitive: true, message: getApiErrorMessage(err, messages.definitiveFallback) };
  }
  return { definitive: false, message: messages.unreachable };
}

/**
 * Why a request failed, from the caller's point of view.
 *
 *   'server'      — we got a response. Its message is written for a human and
 *                   should be shown as-is.
 *   'timeout'     — the request was still open when our clock ran out.
 *   'unreachable' — no response at all: offline, DNS, CORS, or a corporate web
 *                   filter swallowing it.
 *
 * The last two look identical to a user staring at a spinner, but they need
 * different words: "still going" versus "it never left the building".
 */
export type TransportFailure = 'server' | 'timeout' | 'unreachable';

export function classifyTransportError(err: unknown): TransportFailure {
  if (!axios.isAxiosError(err)) return 'unreachable';
  if (err.response) return 'server';
  // Axios reports its own timeout as ECONNABORTED (or ETIMEDOUT on some
  // adapters); anything else without a response never reached us.
  if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') return 'timeout';
  return 'unreachable';
}
